import { plural, t } from '../../i18n';
import { api } from '../../../core/network/api';
import { socket } from '../../../core/network/socket';
import { type ArenaRoomName, DEFAULT_ARENA_ROOM } from '../../arena/room-select.js';
import { stashTicket } from '../../arena/ticket.js';
import { mountDailyReward } from '../daily-reward';
import { mountPlayerDossier, syncQuestBadge } from './play-panels';
import { fetchLiveRooms, populationByMode } from './live-servers';
import { bootIn } from '../fx';
import { playSample, wireMenuSfx } from '../menu-sfx';
import { button, icon, type IconName, plateChrome } from '../ui';
import { mountQueueOverlay, type QueueOverlayHandle } from './queue-overlay';
import type { RouteMount } from './types';
import { enterArena } from '../arena-handoff';

/**
 * The ways to press Play, matching rooms `room-select.ts` whitelists.
 *
 * Every entry mints a ticket for the room's exact mode key. This is not optional politeness:
 * a room with the seam secret configured (the dockerised dev stack, and production) verifies
 * the ticket against its own mode — `verifyTicket(ticket, secret, { mode: this.modeDef.key })`
 * in `arena.room.ts` — so a practice room needs a ticket signed for `tdm_bots`, and the online
 * room one signed for `tdm`. Only `make play` (no secret, room runs open) works unticketed.
 */
interface PlayModeOption {
  room: ArenaRoomName;
  /** Catalogue key for the mode's name — resolved at render time, not at module init. */
  labelKey: string;
  /** Catalogue key for the line shown under the picker once this option is selected. */
  noteKey: string;
  ticketMode: 'tdm' | 'ffa' | 'competitive' | 'tdm_bots' | 'competitive_bots';
  /**
   * Whether pressing PLAY on this option goes through the matchmaking queue.
   *
   * True for every online mode — each has its own lane in `queueModes()`, keyed by the same
   * string as `ticketMode`. False for practice, which is one player and some bots and has
   * nothing to wait for.
   */
  queued: boolean;
  /** The mode's 3D icon on its plate — a picture says "which mode" faster than the name. */
  art: IconName;
}

const PLAY_MODES: PlayModeOption[] = [
  {
    room: 'arena-competitive',
    labelKey: 'play.competitive',
    noteKey: 'play.modes.competitiveNote',
    ticketMode: 'competitive',
    queued: true,
    art: 'trophy',
  },
  {
    room: 'arena-dm',
    labelKey: 'play.deathmatch',
    noteKey: 'play.modes.deathmatchNote',
    ticketMode: 'ffa',
    queued: true,
    art: 'kills',
  },
  {
    room: 'arena',
    labelKey: 'play.teamDeathmatch',
    noteKey: 'play.modes.teamDeathmatchNote',
    ticketMode: 'tdm',
    queued: true,
    art: 'squad',
  },
  {
    room: 'arena-tdm-bots',
    labelKey: 'play.practiceDeathmatch',
    // Explicitly "deathmatch" and "respawn" — a player coming from Competitive's one-life
    // rounds should not have to guess whether dying here ends their game too.
    noteKey: 'play.modes.practiceDeathmatchNote',
    ticketMode: 'tdm_bots',
    queued: false,
    art: 'shield',
  },
  {
    room: 'arena-competitive-bots',
    labelKey: 'play.practiceBomb',
    // The competitive format with nine bots: one life a round, plant and defuse, the side
    // swap. The bots do not plant or defuse yet — the human carries whenever attacking.
    noteKey: 'play.modes.practiceBombNote',
    ticketMode: 'competitive_bots',
    queued: false,
    art: 'bomb',
  },
  // `arena-dm-bots` (practice deathmatch vs bots) exists server-side and stays reachable by
  // URL, but is not offered here: two practice rooms in a four-entry picker is a menu of
  // things to test rather than things to play. The free-for-all colour problem that used to
  // keep every FFA room out of this list is fixed — WELCOME now names the room's mode and the
  // renderer tints by friend-or-foe rather than slot parity (`renderTeamFor`).
];

/** Settings live in localStorage by decision — same key shape as `arena/ticket.ts`'s. */
const MODE_STORAGE_KEY = 'blackline:play-mode';

/**
 * What a player who has never chosen sees selected.
 *
 * Deathmatch, not `DEFAULT_ARENA_ROOM` (the TDM room) and not the first entry in the list
 * (Competitive): a first press of PLAY should not enter a ranked match with rank points on
 * it and eleven other people waiting on a twelve-round commitment. Casual respawn deathmatch
 * is the mode you can leave.
 */
const DEFAULT_PLAY_ROOM: ArenaRoomName = 'arena-dm';

function readStoredRoom(): ArenaRoomName {
  try {
    // `window.localStorage`, never the bare global: Node 25 ships its own `localStorage`
    // that shadows jsdom's in the test worker and lacks a backing file there.
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    // Validated against what the picker OFFERS, not against the arena whitelist: a room can be
    // joinable by URL yet deliberately absent here (`arena-dm-bots`, see PLAY_MODES), and a
    // stored preference for it must not leave the radio group with no selected entry.
    return stored !== null && PLAY_MODES.some((mode) => mode.room === stored)
      ? (stored as ArenaRoomName)
      : DEFAULT_PLAY_ROOM;
  } catch {
    // Private browsing and some embedded webviews refuse localStorage — same fallback
    // `ticket.ts` takes for sessionStorage: default rather than throw.
    return DEFAULT_PLAY_ROOM;
  }
}

function storeRoom(room: ArenaRoomName): void {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, room);
  } catch {
    // Losing the remembered choice is fine; breaking the picker over it is not.
  }
}

/**
 * The Play/home route — the menu itself (design/gdd/meta-visual-language.md, all of it).
 *
 * This is the one route the visual-language pass calls out as structurally different from the
 * other eight: "their menu is the 3D game world ... ours is a document." The fix is not a panel
 * in the middle of the screen — it is leaving the middle empty and anchoring every control to an
 * edge, the same way `shell.ts`'s floating chrome (§2b: nav text top-left, currencies top-right,
 * no bar behind either) does above this content.
 *
 * Every element below is `position: fixed`, deliberately, rather than a "full bleed" mode on
 * `shell.ts`'s `main`/`outlet` (which stay the padded, centred column every other route uses
 * unchanged). Fixed positioning is relative to the viewport regardless of the outlet's own
 * padding or max-width, so this route can bleed to every screen edge without shell.ts having to
 * grow a second layout mode — one property beats a conditional class on a shared container. It
 * also means these nodes still live in, and are torn down by, the outlet's normal lifecycle:
 * `mountShell`'s `setRoute` clears `outlet.innerHTML` on every navigation away exactly as before.
 *
 * The 3D backdrop no longer lives here: it is the SHELL's floor now (`shell.ts` mounts
 * `#menu-backdrop` once, persistent behind every route — see its doc comment and the
 * superseded-§2 note in design/gdd/meta-visual-language.md). This route keeps only its own
 * edge-anchored controls, at z-15 per shell.ts's z-index ladder, floating over that scene.
 */
export const mountPlayRoute: RouteMount = (outlet, ctx) => {
  const root = document.createElement('div');
  outlet.append(root);

  // ── Bottom-centre: "CHANGE SKIN" — the one affordance the spec asks for even before the
  // character render lands, so the slot isn't just a blank hook. ─────────────────────────────
  // Positioning lives on a plain wrapper `div`, never on the `button()` element itself: every
  // button carries `relative` from `BUTTON_BASE`, and Tailwind resolves same-property conflicts
  // (`relative` vs `fixed`) by each utility's position in the generated stylesheet, not by the
  // order class names happen to appear in an element's `class` attribute — so a button styled
  // `"fixed ..."` through its own `className` can silently stay `position: relative` depending
  // on Tailwind's internal ordering. `stack` below never hit this because it is a `div`, not a
  // `button()`; wrapping fixes it the same way for these two.
  // Desktop only, and that is a bug fix rather than a trim.
  //
  // At 375px this sat at `bottom: 76px + inset + 208px` while the action stack starts at
  // `+12px` and grows UPWARD past 208px — four 44px mode buttons, a 92px PLAY and three notes.
  // The two overlapped: the mobile screenshot has "CHANGE SKIN" drawn straight across the
  // DEATHMATCH button, two 44px tap targets sharing pixels, with whichever is later in the DOM
  // silently winning the tap. The control is not lost on phones — it navigates to the Armoury,
  // which is one entry in the bottom nav's More sheet, and the Armoury is where you change a
  // skin anyway.
  const changeSkinWrap = document.createElement('div');
  changeSkinWrap.className = 'hidden md:block fixed z-[15] left-1/2 -translate-x-1/2 md:bottom-[224px]';
  const changeSkin = button(t('play.changeSkin'), {
    variant: 'secondary',
    skewed: true,
    className: 'px-6',
    onClick: () => ctx.navigate('/loadout'),
  });
  wireMenuSfx(changeSkin);
  changeSkinWrap.append(changeSkin);

  // ── Bottom-left: community link ──────────────────────────────────────────
  // Honest placeholder, not a fabricated destination — Black Line has no community platform
  // integrated yet (same "not built" treatment `placeholderRoute` gives Loadout/Shop/Settings),
  // so this stays disabled with a tooltip rather than linking somewhere that doesn't exist.
  const communityWrap = document.createElement('div');
  communityWrap.className =
    'fixed z-[15] left-3 sm:left-[var(--stage-inset)] bottom-[calc(76px+env(safe-area-inset-bottom)+12px)] md:bottom-6 ' +
    'flex flex-col items-start gap-2';
  const community = button(t('play.community'), {
    variant: 'ghost',
    disabled: true,
    ariaLabel: t('play.communitySoonAria'),
    // A disabled placeholder does not earn a row on a phone: below `sm` the daily-reward plate
    // and PLAY already share the 390px, and this only pushed the plate up into the mode notes.
    // `max-sm:hidden`, not `hidden sm:inline-flex`: the ghost variant already carries
    // `inline-flex`, and two bare display utilities resolve by stylesheet order, not by the
    // class list — the variant outranks both.
    className: 'max-sm:hidden',
  });
  community.title = t('play.communitySoon');

  // ── Above Community: the login streak. ────────────────────────────────────
  // In this column rather than a column of its own so the left edge stays one stack, mirroring
  // the right-hand action stack. It appends its own button and hides itself until the status
  // arrives, so a backend that is down or a player with no streak yet leaves this corner
  // exactly as it was.
  const daily = mountDailyReward();

  communityWrap.append(daily.element, community);

  // ── Bottom-right: the action stack — a secondary control directly above one enormous PLAY. ──
  // `flex-col-reverse` so the DOM order can put PLAY first — the button a test (or a screen
  // reader tabbing forward) reaches first should be the one thing this whole route exists for,
  // even though visually the secondary mode row sits above it, per the spec's "secondary actions
  // stack directly above" PLAY.
  const stack = document.createElement('div');
  stack.className =
    'fixed z-[15] right-3 sm:right-[var(--stage-inset)] bottom-[calc(76px+env(safe-area-inset-bottom)+12px)] md:bottom-6 ' +
    // 172px below `sm`, not 200: on a 390px phone the daily-reward plate on the left runs to
    // ~202px, and 200 + 12px inset put PLAY's left edge under it (iPhone 12, 2026-09-14). PLAY's
    // own width is the plate's `min-width: 2.4 × height` — see `.play-plate` in app.css.
    'flex flex-col-reverse items-stretch gap-3 w-[172px] sm:w-[280px] md:w-[368px]';

  // The sheet's action column is the mode wedge and PLAY, nothing else — the standing
  // "60Hz authoritative arena" caption was the loudest of the extras the 1:1 comparison
  // flagged, and it said the same thing every day. The line now exists ONLY while there is a
  // real population to report: live information earns the space, a slogan does not.
  const subtitle = document.createElement('p');
  subtitle.className = 'hidden sm:block kit-chip [--kh:28px] self-end text-[11px] kit-label mb-1';
  subtitle.style.display = 'none';

  /** Repaints the "N playing now" line above the picker. Hidden when the lobby is empty. */
  function paintPopulation(): void {
    if (totalOnline > 0) {
      subtitle.textContent = plural('play.playingNow', totalOnline);
      subtitle.style.display = '';
    } else {
      subtitle.style.display = 'none';
    }
  }

  // ── Mode picker: ONE resting control, not five.
  //
  // Three online modes, each with its own matchmaking lane and its own signed ticket, plus two
  // solo-practice-vs-bots rooms — and until 2026-09-10 all five were on screen at all times as
  // a column of stacked plates, four of which existed only to be *not* chosen. On a wide
  // desktop that column was most of the screen's height. It is now one
  // stack plate showing the chosen mode — with that mode's live population still on its face —
  // which opens a listbox of the five.
  //
  // A listbox rather than a native `<select>`, for the same reason `i18n/lang-pick.ts` is one:
  // the stack's controls are rendered plates (`plate-key`) and a select cannot wear one. That
  // trade costs the keyboard behaviour a select gives for free, so it is implemented below to
  // match lang-pick exactly — Enter/Space to open, arrows and Home/End to move, Enter/Space to
  // choose, Escape to close and hand focus back to the trigger, an outside `pointerdown` to
  // dismiss, and a disposer that unbinds all of it.
  let selectedRoom: ArenaRoomName = readStoredRoom();

  /**
   * Players currently in a room of each mode, from `GET /api/match/servers`.
   *
   * The registry has existed since P4 and no client had ever read it, so the four mode buttons
   * were four identical dark slabs with nothing to choose between them — the single emptiest
   * decision in the menu. A live count is the one piece of information that actually decides
   * which mode to queue, and it is the truth rather than a marketing number: an empty registry
   * shows nothing at all rather than a fabricated population.
   */
  let population = new Map<string, number>();
  let totalOnline = 0;

  /** The trigger and its list share one positioning context; `relative` is the anchor. */
  const modePick = document.createElement('div');
  modePick.className = 'relative w-full';
  modePick.dataset.slot = 'mode-pick';

  /**
   * The open list: a small painted window (`kit-window`) holding one steel plate per mode, so it
   * belongs to the key it springs from rather than reading as a browser dropdown.
   *
   * It opens UPWARD (`bottom: 100%`): the action stack is pinned to the bottom of the
   * viewport, so a list dropping down would open off-screen. `w-full min-w-[260px]` and
   * anchored `right-0` inside a right-anchored stack — at 375px the stack is 172px wide, so
   * the panel leans left over the empty middle of the screen instead of past the right edge,
   * and cannot cause horizontal scroll at any width.
   */
  const modeMenu = document.createElement('div');
  modeMenu.setAttribute('role', 'listbox');
  modeMenu.setAttribute('aria-label', t('play.mode'));
  modeMenu.hidden = true;
  modeMenu.className =
    'kit-window [--kw-rim:20px] [--kw-pad:18px_16px] absolute right-0 bottom-[calc(100%+10px)] z-40 ' +
    'w-full min-w-[260px] flex flex-col gap-2 kit-pop';

  const OPTION_BASE =
    'kit-plate kit-plate-hit [--kp-rim:10px] [--kp-pad:6px_12px] flex items-center justify-between gap-3 w-full min-h-[44px] text-left ' +
    'font-heading font-bold uppercase tracking-[0.1em] text-[12px]';

  /** One plate per mode, built once. `paintModePick` re-labels and re-styles them. */
  const modeOptions = PLAY_MODES.map((mode) => {
    const option = document.createElement('div');
    option.setAttribute('role', 'option');
    option.tabIndex = -1;
    option.dataset.room = mode.room;
    option.className = OPTION_BASE;
    const name = document.createElement('span');
    name.dataset.slot = 'mode-option-label';
    name.className = 'kit-label truncate';
    const lead = document.createElement('span');
    lead.className = 'flex items-center gap-2 min-w-0';
    lead.append(icon(mode.art, { size: 30 }), name);
    const count = document.createElement('span');
    count.dataset.slot = 'mode-option-count';
    count.className = 'kit-chip [--kh:24px] shrink-0 font-heading text-[11px] tabular-nums kit-label';
    option.append(lead, count);
    modeMenu.append(option);
    wireMenuSfx(option);
    return option;
  });

  /**
   * The pip and figure, on the trigger and on every option — and only when there is
   * somebody to count. A "0" beside a mode is worse than no number: it reads as a dead game
   * where a missing number reads as an unknown one.
   */
  function fillCount(host: HTMLElement, playing: number): void {
    if (playing <= 0) {
      host.replaceChildren();
      host.style.display = 'none';
      return;
    }
    const value = document.createElement('span');
    value.textContent = String(playing);
    host.replaceChildren(icon('squad', { size: 16 }), value);
    host.style.display = '';
  }

  const modeNote = document.createElement('p');
  modeNote.className = 'kit-well text-[11px] text-text-dim text-center leading-tight px-3 py-1.5';

  // ══ The action stack is painted keys (`plate-key`, kit.css): the mode trigger is a steel key
  // wearing the chosen mode's 3D icon, name, live count and a caret; PLAY is the one teal hero
  // plate. The old slanted wall of wedge plates is gone — the art has one shape.
  const MODE_TRIGGER_H = 58;
  const PLAY_TEXT_IDLE = 'text-[30px] sm:text-[52px] md:text-[62px] tracking-[0.02em] leading-none';
  // One line: "FINDING A MATCH…" at 26px broke into three lines and spilled off the plate.
  const PLAY_TEXT_BUSY = 'text-[14px] sm:text-[18px] md:text-[20px] leading-none whitespace-nowrap';
  const modeTrigger = document.createElement('button');
  modeTrigger.type = 'button';
  const triggerIcon = icon(PLAY_MODES[0]!.art, { size: 34 });
  const triggerName = document.createElement('span');
  triggerName.dataset.slot = 'mode-trigger-label';
  // 11px below `sm`: in a 172px stack minus the icon and the caret, "DEATHMATCH" at 13px
  // truncated to "DEATHM…" (iPhone 12, 2026-09-14).
  triggerName.className = 'truncate text-[11px] sm:text-[15px] tracking-normal sm:tracking-[0.1em] font-heading font-bold uppercase';
  const triggerCount = document.createElement('span');
  triggerCount.dataset.slot = 'mode-trigger-count';
  triggerCount.className = 'kit-chip [--kh:24px] font-heading text-[11px] tabular-nums';
  const triggerCaret = icon('prev', { size: 22, className: 'motion-safe:transition-transform motion-safe:duration-150' });
  const triggerLabel = document.createElement('span');
  triggerLabel.className = 'flex items-center justify-between gap-2 w-full min-w-0';
  const triggerLead = document.createElement('span');
  triggerLead.className = 'flex items-center gap-2 min-w-0';
  triggerLead.append(triggerIcon, triggerName);
  const triggerRight = document.createElement('span');
  triggerRight.className = 'inline-flex items-center gap-1.5 shrink-0';
  triggerRight.append(triggerCount, triggerCaret);
  triggerLabel.append(triggerLead, triggerRight);
  plateChrome(modeTrigger, triggerLabel, { variant: 'selector', size: 'lg', className: 'w-full' });
  // Inline, like every stack plate: the 44px floor is a number a test and a reader can check.
  modeTrigger.style.setProperty('--pk-h', `${MODE_TRIGGER_H}px`);
  modeTrigger.style.height = `${MODE_TRIGGER_H}px`;
  modeTrigger.setAttribute('aria-haspopup', 'listbox');
  modeTrigger.setAttribute('aria-expanded', 'false');
  wireMenuSfx(modeTrigger);

  let modeOpen = false;

  /** Repaints trigger, options and the note from `selectedRoom` + `population`. */
  function paintModePick(): void {
    const selected = PLAY_MODES.find((mode) => mode.room === selectedRoom) ?? PLAY_MODES[0]!;
    const selectedLabel = t(selected.labelKey);
    const selectedPlaying = population.get(selected.ticketMode) ?? 0;
    triggerName.textContent = selectedLabel;
    triggerIcon.src = `/game/ui/icons/${selected.art}.webp`;
    // The count stays readable with the list shut — it is the one number that actually decides
    // which mode to queue, and hiding it behind a click would undo the reason it was added.
    fillCount(triggerCount, selectedPlaying);
    modeTrigger.setAttribute(
      'aria-label',
      selectedPlaying > 0
        ? t('play.modeAria', { mode: selectedLabel, count: selectedPlaying })
        : selectedLabel,
    );
    modeTrigger.title = t('play.modeChange');
    triggerCaret.style.transform = modeOpen ? 'rotate(-90deg)' : 'rotate(90deg)';

    modeOptions.forEach((option, index) => {
      const mode = PLAY_MODES[index]!;
      const active = mode.room === selectedRoom;
      const label = t(mode.labelKey);
      const playing = population.get(mode.ticketMode) ?? 0;
      option.setAttribute('aria-selected', String(active));
      option.className = `${OPTION_BASE} ${active ? 'kit-selected' : ''}`;
      (option.querySelector('[data-slot="mode-option-label"]') as HTMLElement).textContent = label;
      fillCount(option.querySelector('[data-slot="mode-option-count"]') as HTMLElement, playing);
      if (playing > 0) {
        option.setAttribute('aria-label', t('play.modeAria', { mode: label, count: playing }));
      } else {
        option.removeAttribute('aria-label');
      }
    });

    modeNote.textContent = t(selected.noteKey);
  }

  // ── Keyboard and dismissal, matching `lang-pick.ts` step for step ───────────────────────
  const activeModeIndex = (): number => {
    const focused = modeOptions.findIndex((option) => option === document.activeElement);
    if (focused >= 0) return focused;
    const selected = PLAY_MODES.findIndex((mode) => mode.room === selectedRoom);
    return selected >= 0 ? selected : 0;
  };

  const focusModeOption = (index: number): void => {
    modeOptions[(index + modeOptions.length) % modeOptions.length]?.focus();
  };

  const setModeOpen = (next: boolean, focusTrigger = false): void => {
    modeOpen = next;
    modeMenu.hidden = !next;
    modeTrigger.setAttribute('aria-expanded', next ? 'true' : 'false');
    triggerCaret.style.transform = next ? 'rotate(-90deg)' : 'rotate(90deg)';
    if (next) focusModeOption(activeModeIndex());
    else if (focusTrigger) modeTrigger.focus();
  };

  const chooseMode = (room: ArenaRoomName): void => {
    setModeOpen(false, true);
    if (room === selectedRoom) return;
    selectedRoom = room;
    storeRoom(selectedRoom);
    paintModePick();
  };

  const onTriggerClick = (): void => { setModeOpen(!modeOpen); };

  const onTriggerKey = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown'
      || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setModeOpen(true);
    } else if (event.key === 'Escape' && modeOpen) {
      event.preventDefault();
      setModeOpen(false, true);
    }
  };

  const onModeMenuKey = (event: KeyboardEvent): void => {
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); focusModeOption(activeModeIndex() + 1); break;
      case 'ArrowUp': event.preventDefault(); focusModeOption(activeModeIndex() - 1); break;
      case 'Home': event.preventDefault(); focusModeOption(0); break;
      case 'End': event.preventDefault(); focusModeOption(modeOptions.length - 1); break;
      case 'Escape': event.preventDefault(); setModeOpen(false, true); break;
      case 'Tab': setModeOpen(false); break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const room = (event.target as HTMLElement | null)?.dataset?.room;
        if (room) chooseMode(room as ArenaRoomName);
        break;
      }
      default: break;
    }
  };

  const onModeMenuClick = (event: Event): void => {
    const room = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-room]')
      ?.dataset.room;
    if (room) chooseMode(room as ArenaRoomName);
  };

  // `pointerdown`, not `click`: a touch that starts outside the list is already a dismissal,
  // and waiting for the click lets the same tap land on whatever is under the list too.
  const onDocPointerDown = (event: Event): void => {
    if (!modeOpen) return;
    if (!modePick.contains(event.target as Node)) setModeOpen(false);
  };

  modeTrigger.addEventListener('click', onTriggerClick);
  modeTrigger.addEventListener('keydown', onTriggerKey);
  modeMenu.addEventListener('keydown', onModeMenuKey);
  modeMenu.addEventListener('click', onModeMenuClick);
  document.addEventListener('pointerdown', onDocPointerDown, true);

  const disposeModePick = (): void => {
    document.removeEventListener('pointerdown', onDocPointerDown, true);
    modeTrigger.removeEventListener('click', onTriggerClick);
    modeTrigger.removeEventListener('keydown', onTriggerKey);
    modeMenu.removeEventListener('keydown', onModeMenuKey);
    modeMenu.removeEventListener('click', onModeMenuClick);
  };

  modePick.append(modeTrigger, modeMenu);
  paintModePick();

  // PLAY: the hero. A teal painted plate (`play-plate`, kit.css: 70px on a phone, 150px from
  // `sm`) with the 3D play icon bobbing beside the word. The label keeps `data-plate-label`;
  // the word lives in its own span so a busy label swap never deletes the icon.
  const play = document.createElement('button');
  play.type = 'button';
  const playIcon = icon('play', { size: 64, className: 'kit-float max-sm:w-8! max-sm:h-8! [filter:drop-shadow(0_3px_4px_rgba(0,0,0,0.6))]' });
  const playText = document.createElement('span');
  playText.textContent = t('play.title');
  const playFace = document.createElement('span');
  playFace.className = 'inline-flex items-center gap-2 sm:gap-3';
  playFace.append(playIcon, playText);
  plateChrome(play, playFace, { variant: 'primary', size: 'hero', className: 'w-full disabled:opacity-60' });
  play.classList.add('play-plate', 'motion-safe:animate-hero-breathe');
  const playLabelEl = play.querySelector('.plate-key-label') as HTMLElement;
  playLabelEl.dataset.plateLabel = '';
  playText.className = PLAY_TEXT_IDLE;
  // PLAY sounds heavy; everything else on the stack clicks. Synthesized, volume-governed,
  // mouse-only hover ticks — see menu-sfx.ts.
  wireMenuSfx(play, 'heavy');

  const playNote = document.createElement('p');
  playNote.className = 'kit-well text-[12px] text-text-dim text-center px-3 py-1.5 empty:hidden';

  /** Resets PLAY to its idle, pressable state after a queue abort or fallback note. */
  function resetPlay(): void {
    play.disabled = false;
    playText.textContent = t('play.title');
    playText.className = PLAY_TEXT_IDLE;
    playIcon.hidden = false;
  }

  /** Today's direct deep-join — the whole launch path before matchmaking existed, and still
   * the only path for practice modes and for `make play` (no backend, no queue). */
  async function directLaunch(mode: PlayModeOption): Promise<void> {
    if (mode.ticketMode) {
      try {
        const minted = await api.post<{ ticket: string; expiresAt: string }>('/match/ticket', {
          mode: mode.ticketMode,
        });
        stashTicket(minted);
      } catch (error) {
        // Deliberately not fatal — see the equivalent note this replaces in the old
        // `bootstrap.ts`. A room with no ticket secret configured runs open, which is the local
        // setup, and refusing to launch there would make the game unplayable for the common case.
        playNote.textContent = t('play.status.noTicket', { message: (error as Error).message });
      }
    }
    // The default room needs no query string at all — `resolveArenaRoom` already falls back to
    // it — so the common case (Online) keeps the exact URL it always has. The navigation goes
    // through `enterArena`, which covers the document swap with the arena's own loading screen
    // and warms its bundle and assets first — see `shell/arena-handoff.ts`.
    enterArena(mode.room === DEFAULT_ARENA_ROOM ? '/arena.html' : `/arena.html?room=${mode.room}`);
  }

  let queueHandle: QueueOverlayHandle | null = null;

  /** How long PLAY waits for the meta socket to come up before calling matchmaking offline. */
  const SOCKET_WAIT_MS = 5_000;

  /**
   * Resolves true once the meta socket is connected, false after `SOCKET_WAIT_MS`. Nudges a
   * disconnected socket to (re)connect first — the reconnect loop is core's, but a player who
   * just pressed PLAY should not wait for its next backoff step.
   */
  function awaitSocket(): Promise<boolean> {
    if (socket.connected) return Promise.resolve(true);
    return new Promise((resolve) => {
      const done = (ok: boolean): void => {
        clearTimeout(timer);
        socket.off('connect', onConnect);
        resolve(ok);
      };
      const onConnect = (): void => done(true);
      const timer = setTimeout(() => done(false), SOCKET_WAIT_MS);
      socket.on('connect', onConnect);
      (socket as { connect?: () => unknown }).connect?.();
    });
  }

  async function launch(): Promise<void> {
    playSample('start');
    play.disabled = true;
    playText.textContent = t('play.findingMatch');
    playText.className = PLAY_TEXT_BUSY;
    // The words need the whole face: beside the icon "FINDING A MATCH…" ran off the plate.
    playIcon.hidden = true;
    playNote.textContent = '';

    const mode = PLAY_MODES.find((candidate) => candidate.room === selectedRoom) ?? PLAY_MODES[0]!;

    // Practice is one player and some bots: no queue, straight in.
    if (!mode.queued) {
      await directLaunch(mode);
      return;
    }

    // An online mode goes through the queue or not at all. This used to fall back to a direct
    // launch when the meta socket was down — and on production (2026-09-11) that put a player
    // who pressed Competitive alone in an empty public room, "waiting for other players"
    // forever: the socket never connects there while the frontend is pointed at the API host
    // (core's `socket.ts` switches to `/socket.io` when `API_URL` is set, and the gateway is
    // on `/ws`). Bots only ever fill an ARRANGED room, so a lonely direct launch is a dead
    // room by construction. Wait for the socket a moment, then say what is wrong.
    if (!(await awaitSocket())) {
      playNote.textContent = t('play.status.offline');
      resetPlay();
      return;
    }

    queueHandle = mountQueueOverlay({
      socket,
      username: ctx.user.username ?? ctx.user.email,
      // The lane, keyed by the same string the ticket is signed for. `mm:start` names the
      // room it minted for, so the navigation below follows the server rather than assuming.
      mode: mode.ticketMode,
      onStart: (message) => {
        queueHandle = null;
        stashTicket({ ticket: message.ticket, expiresAt: message.expiresAt });
        // The room comes from the server's own start message, not from the picker: the queue
        // decides which lane seated the group, and a client that assumed `arena` here would
        // walk a competitive group into the casual room its ticket is not signed for.
        const room = message.room === DEFAULT_ARENA_ROOM ? '' : `&room=${encodeURIComponent(message.room)}`;
        // The path that matters most for the hand-off: ten accepted clients navigate within a
        // second of each other and hit the asset host together.
        enterArena(`/arena.html?match=${encodeURIComponent(message.matchId)}${room}`);
      },
      onAborted: (reason) => {
        queueHandle = null;
        playNote.textContent =
          reason === 'cancelled'
            ? t('play.status.cancelled')
            : reason === 'declined'
              ? t('play.status.declined')
              : reason === 'timeout'
                ? t('play.status.expired')
                : reason === 'cooldown'
                  ? t('play.status.cooldown')
                  : t('play.status.error');
        resetPlay();
      },
      onFallback: () => {
        // A connected socket whose gateway never answered `mm:queue` — same dead room if we
        // launched alone, so the same honest note and a pressable PLAY instead.
        queueHandle = null;
        playNote.textContent = t('play.status.offline');
        resetPlay();
      },
    });
  }

  /**
   * Reads the registry now and every 20s while this route is mounted.
   *
   * Polled rather than pushed: the registry is a Redis key set written by the realtime tier's
   * heartbeat, there is no meta-side event when a room's population changes, and a socket
   * subscription for a number on a menu is not worth a gateway. 20s is well inside the
   * registry's own expiry and slow enough that the count never flickers under the cursor.
   */
  let populationTimer: ReturnType<typeof setInterval> | null = null;
  async function refreshPopulation(): Promise<void> {
    const rooms = await fetchLiveRooms();
    const counted = populationByMode(rooms);
    population = counted.byMode;
    totalOnline = counted.total;
    paintPopulation();
    paintModePick();
  }
  void refreshPopulation();
  populationTimer = setInterval(() => void refreshPopulation(), 20_000);

  play.addEventListener('click', () => void launch());

  // Source order is bottom-to-top under `flex-col-reverse`: playNote (bottom-most), then PLAY,
  // then the mode note, then the mode picker, then the subtitle (top-most) — see the note on
  // `stack` above.
  stack.append(playNote, play, modeNote, modePick, subtitle);

  // The left column is the dossier and nothing else — 1:1 with the lobby sheet. The quest
  // fetch that used to ride on the objectives panel survives headless, because it is the only
  // publisher of the Quests nav dot; see `play-panels.ts`.
  const dossier = mountPlayerDossier();
  const disposeQuestBadge = syncQuestBadge();

  root.append(stack, changeSkinWrap, communityWrap, dossier.element);

  // `/play` was the one route with zero mount motion at either width — measured — which is a
  // strange thing for the screen the game opens on. It is excluded from the shell's own route
  // reveal by design (its content is fixed furniture around a live 3D scene, not a column in
  // an outlet), so it gets its own: the action stack rises once, on arrival. The two left-hand
  // panels animate themselves when their fetches land, which is later and is the right moment
  // for them — this is the corner the player's eye goes to.
  // ── The power-on. One orchestrated sequence, not scattered effects: the mode plate slides in
  // from the right and PLAY lands last with a touch of overshoot —
  // the loudest object arrives loudest. Three beats now rather than seven: the picker is one
  // plate, so the rank-by-rank stagger has nothing left to stagger. The dossier runs its own boot from the
  // left when its data lands (play-panels.ts), so the two sides of the screen come up the way
  // a cockpit does: instruments first, master arm last. All of it via bootIn/revealOnMount,
  // which no-op under reduced motion and leave everything visible if WAAPI is missing.
  requestAnimationFrame(() => {
    bootIn(modePick, { fromX: 26, delayMs: 0, durationMs: 420 });
    bootIn(play, { fromY: 18, delayMs: 230, durationMs: 540, overshoot: true });
  });

  return () => {
    // Navigating away while queued IS leaving the queue — the overlay's disposer emits
    // mm:cancel and detaches its socket listeners.
    queueHandle?.dispose();
    queueHandle = null;
    disposeModePick();
    if (populationTimer !== null) clearInterval(populationTimer);
    populationTimer = null;
    changeSkinWrap.remove();
    dossier.dispose();
    disposeQuestBadge();
    daily.dispose();
    communityWrap.remove();
    stack.remove();
    root.remove();
  };
};
